import { useMemo, type JSX, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { useOutreacherInsights } from '@/hooks/useInsights';
import { useUrlState } from '@/hooks/useUrlState';
import type { OutreacherInsightRow, OutreachersResponse } from '@/api/insights';
import { ratio } from '@/lib/chart';
import { daysSince, formatDate } from '@/lib/dates';
import { insightsInvitesHref, JOINED_STAGES, money, POSTED_STAGES, REPLIED_STAGES, toNum } from '@/lib/insights';
import { cn } from '@/lib/utils';
import { useInsightsScope } from './scope';
import {
  CountLink,
  LoadError,
  Loading,
  Note,
  RateValue,
  Section,
  SortHeader,
  Th,
  Unavailable,
  useSort,
  useSorted,
} from './parts';

type SortKey =
  | 'name'
  | 'messages'
  | 'replied'
  | 'replyRate'
  | 'joinedCohort'
  | 'joinRate'
  | 'postedCohort'
  | 'postRate'
  | 'cashPaid'
  | 'owedNow'
  | 'costPerPost'
  | 'proofFailRate'
  | 'duplicateCreators'
  | 'lastLoggedAt';

const FALLBACK_DEFS: Record<string, string> = {
  messages: '“I messaged them” records this outreacher made in this period.',
  replied: 'Invites they logged in this period, not rejected or hidden.',
  replyRate: 'Their replies after a recorded message ÷ their messages.',
  joinedCohort: 'Of their replies in this period, how many have joined since.',
  joinRate: 'Joined ÷ replies, for tracked teams.',
  postedCohort: 'Of their replies in this period, how many have posted since.',
  postRate: 'Posted ÷ replies, for tracked teams.',
  cashPaid: 'Payouts to them marked paid in this period, prizes and retainers included.',
  owedNow: 'What the ledger says they are owed right now, across every period. Payroll uses an older formula, so it can differ.',
  costPerPost: 'Cash paid to them ÷ their posts in this period.',
  proofFailRate: 'Proof screenshots reviewed in this period and rejected ÷ reviewed.',
  duplicateCreators: 'Creators they logged who are also on another valid invite.',
  lastLoggedAt: 'When they last logged a reply.',
};

/** Everyone in scope added up (inactive outreachers included), under the rows. Hidden on phones. */
function TotalsRow({ totals: t, showTeam }: { totals: OutreachersResponse['totals'] | undefined; showTeam: boolean }): JSX.Element | null {
  if (!t) return null;
  const cell = 'px-3 py-2.5 text-right font-mono font-semibold';
  return (
    <tfoot className="hidden border-t-2 border-border sm:table-footer-group">
      <tr>
        <th scope="row" className="px-3 py-2.5 text-left text-sm font-semibold">
          Everyone in scope
        </th>
        {showTeam ? <td /> : null}
        <td className={cell}>{t.messages.toLocaleString()}</td>
        <td className={cell}>{t.replied.toLocaleString()}</td>
        <td className={cell}>
          <RateValue rate={t.replyRate} n={t.messages} of="messages" reason="No messages recorded" />
        </td>
        <td className={cell}>{t.joinedCohort.toLocaleString()}</td>
        <td className={cell}>
          <RateValue rate={t.joinRate} n={t.repliedTracked} reason="No tracked replies" />
        </td>
        <td className={cell}>{t.postedCohort.toLocaleString()}</td>
        <td className={cell}>
          <RateValue rate={t.postRate} n={t.repliedTracked} reason="No tracked replies" />
        </td>
        <td className={cell}>{money(t.cashSpent)}</td>
        <td className={cell}>{t.costPerPost == null ? <Unavailable reason="No posts in this period" /> : money(t.costPerPost)}</td>
        <td className={cell}>{money(t.owedNow)}</td>
        <td colSpan={3} />
      </tr>
    </tfoot>
  );
}

/** Idle this many days and the last-logged date is flagged. */
const IDLE_DAYS = 14;

const replyRateOf = (r: OutreacherInsightRow): number | null => r.replyRate ?? ratio(r.repliedAfterMessage, r.messages);

function valueOf(r: OutreacherInsightRow, key: SortKey): number | string | null {
  switch (key) {
    case 'name':
      return r.name.toLowerCase();
    case 'replyRate':
      return replyRateOf(r);
    case 'cashPaid':
    case 'owedNow':
    case 'costPerPost':
      return toNum(r[key]);
    case 'lastLoggedAt':
      return r.lastLoggedAt ? new Date(r.lastLoggedAt).getTime() : null;
    default:
      return r[key];
  }
}

/** The denominator behind each rate column, for the thin-sample ordering. */
function sampleOf(r: OutreacherInsightRow, key: SortKey): number | undefined {
  switch (key) {
    case 'replyRate':
      return r.messages;
    case 'joinRate':
    case 'postRate':
      return r.replied;
    case 'proofFailRate':
      return r.proofReviewed;
    default:
      return undefined;
  }
}

export function OutreachersTab(): JSX.Element {
  const scope = useInsightsScope();
  const q = useOutreacherInsights(scope.params);
  const [url, setUrl] = useUrlState({ inactive: false as boolean });
  const [sort, toggleSort] = useSort<SortKey>({ key: 'postedCohort', dir: 'desc' });
  const navigate = useNavigate();

  const all = q.data?.rows ?? [];
  const inactiveCount = all.filter((r) => !r.isActive).length;
  const visible = useMemo(() => (url.inactive ? all : all.filter((r) => r.isActive)), [all, url.inactive]);
  const rows = useSorted(visible, sort, valueOf, sampleOf);

  if (q.isLoading) return <Loading label="Loading outreachers…" />;
  if (q.isError || !q.data) return <LoadError error={q.error} what="outreachers" />;

  const defs = q.data.definitions ?? {};
  const def = (key: string): string | undefined => defs[key] ?? FALLBACK_DEFS[key];
  const window = scope.windows.current;
  const logging = visible.filter((r) => r.replied > 0).length;
  const showTeam = !scope.locked;

  const onRowClick = (e: MouseEvent<HTMLTableRowElement>, r: OutreacherInsightRow): void => {
    if ((e.target as HTMLElement).closest('a,button')) return;
    navigate(`/admin/outreachers/${r.publicId}`);
  };

  return (
    <div className="space-y-4">
      <Section
        title="Who turns outreach into posts?"
        description={
          <>
            Rates follow each outreacher’s replies in this period forward. A greyed rate rests on fewer than 10 replies
            (or messages, or reviews), too few to compare. Counts open that outreacher’s invites; select a row for
            their page.
          </>
        }
        actions={
          inactiveCount > 0 ? (
            <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={url.inactive}
                onChange={(e) => setUrl({ inactive: e.target.checked })}
                className="h-4 w-4 rounded border-input accent-[var(--brand)]"
              />
              Show {inactiveCount} inactive
            </label>
          ) : null
        }
      >
        {rows.length === 0 ? (
          <EmptyState
            icon={<Users className="h-5 w-5" />}
            title="No outreachers in this scope"
            hint="Pick another team, or show inactive outreachers."
          />
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              {logging.toLocaleString()} of {visible.length.toLocaleString()} outreachers logged a reply in this period.
            </p>
            <div className="table-frame relative overflow-x-auto">
              <table className="responsive-stack w-full whitespace-nowrap text-sm" aria-label="Outreacher leaderboard">
                <thead>
                  <tr className="text-left">
                    <SortHeader label="Outreacher" k="name" sort={sort} onSort={toggleSort} numeric={false} />
                    {showTeam ? <Th label="Team" numeric={false} /> : null}
                    <SortHeader label="Messages" k="messages" sort={sort} onSort={toggleSort} def={def('messages')} />
                    <SortHeader label="Replies" k="replied" sort={sort} onSort={toggleSort} def={def('replied')} />
                    <SortHeader label="Reply rate*" k="replyRate" sort={sort} onSort={toggleSort} def={def('replyRate')} />
                    <SortHeader label="Joined" k="joinedCohort" sort={sort} onSort={toggleSort} def={def('joinedCohort')} />
                    <SortHeader label="Join rate" k="joinRate" sort={sort} onSort={toggleSort} def={def('joinRate')} />
                    <SortHeader label="Posted" k="postedCohort" sort={sort} onSort={toggleSort} def={def('postedCohort')} />
                    <SortHeader label="Post rate" k="postRate" sort={sort} onSort={toggleSort} def={def('postRate')} />
                    <SortHeader label="Cash paid" k="cashPaid" sort={sort} onSort={toggleSort} def={def('cashPaid')} />
                    <SortHeader label="Cost per post" k="costPerPost" sort={sort} onSort={toggleSort} def={def('costPerPost')} />
                    <SortHeader label="Owed now (ledger)" k="owedNow" sort={sort} onSort={toggleSort} def={def('owedNow')} />
                    <SortHeader label="Proof fail rate" k="proofFailRate" sort={sort} onSort={toggleSort} def={def('proofFailRate')} />
                    <SortHeader label="Duplicates" k="duplicateCreators" sort={sort} onSort={toggleSort} def={def('duplicateCreators')} />
                    <SortHeader label="Last logged" k="lastLoggedAt" sort={sort} onSort={toggleSort} def={def('lastLoggedAt')} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const drill = { team: r.team, inviterId: r.inviterId, window };
                    const idle = daysSince(r.lastLoggedAt);
                    return (
                      <tr
                        key={r.publicId}
                        onClick={(e) => onRowClick(e, r)}
                        className={cn(
                          'cursor-pointer border-t border-border transition-colors hover:bg-accent/40',
                          !r.isActive && 'opacity-60',
                        )}
                      >
                        <td className="px-3 py-2 font-medium">
                          <Link to={`/admin/outreachers/${r.publicId}`} className="hover:underline">
                            {r.name}
                          </Link>
                          {!r.isActive ? <span className="ml-1.5 text-xs font-normal text-muted-foreground">inactive</span> : null}
                        </td>
                        {showTeam ? (
                          <td className="px-3 py-2 text-muted-foreground" data-label="Team">
                            {scope.teamName(r.team)}
                          </td>
                        ) : null}
                        <td className="px-3 py-2 text-right font-mono" data-label="Messages">
                          {r.messages.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right" data-label="Replies">
                          <CountLink value={r.replied} to={insightsInvitesHref({ ...drill, stages: REPLIED_STAGES })} />
                        </td>
                        <td className="px-3 py-2 text-right" data-label="Reply rate*">
                          <RateValue rate={replyRateOf(r)} n={r.messages} of="messages" reason="No messages recorded" />
                        </td>
                        <td className="px-3 py-2 text-right" data-label="Joined">
                          <CountLink value={r.joinedCohort} to={insightsInvitesHref({ ...drill, stages: JOINED_STAGES })} />
                        </td>
                        <td className="px-3 py-2 text-right" data-label="Join rate">
                          <RateValue
                            rate={r.joinRate}
                            n={r.replied}
                            reason={r.replied > 0 ? 'Team not tracked: joins are updated by hand' : 'No replies in this period'}
                          />
                        </td>
                        <td className="px-3 py-2 text-right" data-label="Posted">
                          <CountLink value={r.postedCohort} to={insightsInvitesHref({ ...drill, stages: POSTED_STAGES })} />
                        </td>
                        <td className="px-3 py-2 text-right" data-label="Post rate">
                          <RateValue
                            rate={r.postRate}
                            n={r.replied}
                            reason={r.replied > 0 ? 'Team not tracked: posts are updated by hand' : 'No replies in this period'}
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-mono" data-label="Cash paid">
                          {money(r.cashPaid)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono" data-label="Cost per post">
                          {r.costPerPost == null ? <Unavailable reason="No posts in this period" /> : money(r.costPerPost)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono" data-label="Owed now (ledger)">
                          {toNum(r.owedNow) ? (
                            <Link
                              to={insightsInvitesHref({ team: r.team, inviterId: r.inviterId, owed: true })}
                              className="hover:underline"
                              title="Open their owed invites"
                            >
                              {money(r.owedNow)}
                            </Link>
                          ) : (
                            money(r.owedNow)
                          )}
                        </td>
                        <td className="px-3 py-2 text-right" data-label="Proof fail rate">
                          <RateValue
                            rate={r.proofFailRate}
                            n={r.proofReviewed}
                            of="proofs reviewed"
                            reason="No proofs reviewed in this period"
                          />
                        </td>
                        <td
                          className={cn('px-3 py-2 text-right font-mono', r.duplicateCreators > 0 && 'text-pending-strong')}
                          data-label="Duplicates"
                        >
                          {r.duplicateCreators.toLocaleString()}
                        </td>
                        <td
                          className={cn(
                            'px-3 py-2 text-right',
                            idle !== null && idle > IDLE_DAYS ? 'text-pending-strong' : 'text-muted-foreground',
                          )}
                          data-label="Last logged"
                          title={idle !== null ? `${idle} days ago` : 'Never logged a reply'}
                        >
                          {r.lastLoggedAt ? formatDate(r.lastLoggedAt) : 'Never'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <TotalsRow totals={q.data.totals} showTeam={showTeam} />
              </table>
            </div>
          </>
        )}
      </Section>
      <Note>
        * Reply rate only counts messages recorded with “I messaged them”. Last logged turns amber after {IDLE_DAYS} days
        without a reply logged.
      </Note>
    </div>
  );
}
